import { useEffect, useState, useRef } from "preact/hooks";
import DOMPurify from "dompurify";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import { usePromoBannerSettings } from "~/hooks/use-promo-banner-settings.ts";

function PromoBanner() {
	const t = useTranslator();
	const settings = usePromoBannerSettings();
	const [isDismissed, setIsDismissed] = useState(false);
	const bannerRef = useRef<HTMLDivElement>(null);

	// Update body class and CSS variable for header positioning
	useEffect(() => {
		if (!settings || isDismissed || !bannerRef.current) {
			document.body.classList.remove("has-promo-banner");
			document.documentElement.style.removeProperty("--promo-banner-height");
			return;
		}

		document.body.classList.add("has-promo-banner");
		const height = bannerRef.current.offsetHeight;
		document.documentElement.style.setProperty(
			"--promo-banner-height",
			`${height}px`,
		);

		return () => {
			document.body.classList.remove("has-promo-banner");
			document.documentElement.style.removeProperty("--promo-banner-height");
		};
	}, [settings, isDismissed]);

	if (!settings || isDismissed) {
		return null;
	}

	// Sanitize HTML content (defense-in-depth, server already sanitizes)
	// Match API sanitization: allow all default tags except dangerous ones, allow style on all tags
	const sanitizedHtml = DOMPurify.sanitize(settings.htmlContent, {
		// Allow all tags except dangerous ones (matching API: script, iframe, object, embed, form)
		FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
		// Allow style attribute on all tags (matching API), plus common attributes for links, images, etc.
		ALLOWED_ATTR: [
			"style", // Allow style on all tags (matching API)
			"class",
			"href",
			"target",
			"rel",
			"title",
			// Image attributes (matching API)
			"src",
			"alt",
			"width",
			"height",
		],
		ALLOW_DATA_ATTR: false,
	});

	return (
		<div ref={bannerRef} className="promo-banner">
			<div
				className="promo-banner__content"
				dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
			/>
			<button
				type="button"
				className="promo-banner__close-btn"
				onClick={() => setIsDismissed(true)}
				aria-label={t("Close banner")}
			>
				×
			</button>
		</div>
	);
}

export default PromoBanner;
